import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { ITheme } from "../Themes/ThemeTypes";
import { ThemeColorEnum } from "../Themes/ThemeEnums";
import { themeColor } from "../Themes/ThemeFunctions";
import { useIsMobile } from "../Layouts/AppLayout/IsMobileHook";

export type ResponsivePopoverWrapperVariant = "anchored" | "floating";
export type ResponsivePopoverAlign = "left" | "right";

const Wrapper = styled.div<{ theme: ITheme; $variant: ResponsivePopoverWrapperVariant }>`
  ${(props) =>
    props.$variant === "floating"
      ? `position: absolute; top: 12px; left: 12px; z-index: ${props.theme.base.z_index.overlay};`
      : "position: relative; display: inline-block;"}
`;

const fullScreenEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

interface CardProps {
  theme: ITheme;
  $isMobile: boolean;
  $align: ResponsivePopoverAlign;
  $surface: ThemeColorEnum;
  $desktopBorderRadius: number;
  $desktopWidth?: string;
  $desktopMinWidth?: string;
}

const Card = styled.div<CardProps>`
  position: ${(props) => (props.$isMobile ? "fixed" : "absolute")};
  z-index: ${(props) => props.theme.base.z_index.overlay};
  background: ${(props) => themeColor(props.$surface, props.theme)};
  color: rgb(${(props) => props.theme.colors.on_surface});
  font-family: ${(props) => props.theme.base.typography.font};
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${(props) =>
    props.$isMobile
      ? css`
          inset: 0;
          box-sizing: border-box;
          padding: 24px;
          border-radius: 0;
          overflow-y: auto;
          animation: ${fullScreenEnter} ${props.theme.base.motion.duration.medium2}ms
            ${props.theme.base.motion.easing.standard_decelerate};
        `
      : css`
          top: calc(100% + 8px);
          ${props.$align === "left" ? "left: 0;" : "right: 0;"}
          ${props.$desktopWidth ? `width: ${props.$desktopWidth};` : ""}
          ${props.$desktopMinWidth ? `min-width: ${props.$desktopMinWidth};` : ""}
          padding: 16px;
          border-radius: ${props.$desktopBorderRadius}px;
          box-shadow: ${props.theme.base.elevation.level2_shadow};
        `}
`;

interface ResponsivePopoverProps {
  renderTrigger: (api: { open: boolean; toggle: () => void }) => React.ReactNode;
  children: (api: { close: () => void }) => React.ReactNode;
  wrapperVariant?: ResponsivePopoverWrapperVariant;
  desktopAlign?: ResponsivePopoverAlign;
  surfaceColor?: ThemeColorEnum;
  desktopBorderRadius?: number;
  desktopWidth?: string;
  desktopMinWidth?: string;
  onOpenChange?: (open: boolean) => void;
}

const ResponsivePopover: React.FC<ResponsivePopoverProps> = ({
  renderTrigger,
  children,
  wrapperVariant = "anchored",
  desktopAlign = "right",
  surfaceColor = ThemeColorEnum.SURFACE,
  desktopBorderRadius = 28,
  desktopWidth,
  desktopMinWidth,
  onOpenChange,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const onOpenChangeRef = useRef(onOpenChange);
  const notifiedOpenRef = useRef(open);

  const toggle = (): void => setOpen((wasOpen) => !wasOpen);
  const close = (): void => setOpen(false);

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  useEffect(() => {
    if (notifiedOpenRef.current === open) return;
    notifiedOpenRef.current = open;
    onOpenChangeRef.current?.(open);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const closeNow = (): void => setOpen(false);
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") closeNow();
    };
    const handleClick = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        closeNow();
      }
    };
    window.addEventListener("keydown", handleKey);
    window.addEventListener("mousedown", handleClick);
    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  return (
    <Wrapper ref={wrapperRef} $variant={wrapperVariant}>
      {renderTrigger({ open, toggle })}
      {open && (
        <Card
          $isMobile={isMobile}
          $align={desktopAlign}
          $surface={surfaceColor}
          $desktopBorderRadius={desktopBorderRadius}
          $desktopWidth={desktopWidth}
          $desktopMinWidth={desktopMinWidth}
        >
          {children({ close })}
        </Card>
      )}
    </Wrapper>
  );
};

export default ResponsivePopover;
